
import React, { useState } from 'react';
import { useNodes } from '@/hooks/useNodes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Link } from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;

interface NodeEdge {
  targetNodeId: string;
  relationshipType: 'prerequisite' | 'related' | 'deepens' | 'critiques' | 'applies' | 'exemplifies';
  weight?: number;
}

interface NodeConnectionInterfaceProps {
  edges: NodeEdge[];
  onChange: (edges: NodeEdge[]) => void;
}

const relationshipTypes = [
  { value: 'prerequisite', label: 'Prerequisite', description: 'Required before this content' },
  { value: 'related', label: 'Related', description: 'Similar or connected topic' },
  { value: 'deepens', label: 'Deepens', description: 'Builds upon this content' },
  { value: 'critiques', label: 'Critiques', description: 'Challenges or questions this content' },
  { value: 'applies', label: 'Applies', description: 'Practical application of this content' },
  { value: 'exemplifies', label: 'Exemplifies', description: 'Example or illustration of this content' },
];

const NodeConnectionInterface: React.FC<NodeConnectionInterfaceProps> = ({ edges, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRelationType, setSelectedRelationType] = useState<string>('');
  const { data: nodes } = useNodes();

  const filteredNodes = nodes?.filter(node =>
    node.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
    node.status === 'published' &&
    !edges.some(edge => edge.targetNodeId === node.id)
  );

  const addConnection = (targetNode: Node) => {
    if (!selectedRelationType) return;

    const newEdge: NodeEdge = {
      targetNodeId: targetNode.id,
      relationshipType: selectedRelationType as NodeEdge['relationshipType'],
      weight: 1,
    };

    onChange([...edges, newEdge]);
    setSearchTerm('');
  };

  const removeConnection = (targetNodeId: string) => {
    onChange(edges.filter(edge => edge.targetNodeId !== targetNodeId));
  };

  const getNodeById = (nodeId: string) => {
    return nodes?.find(node => node.id === nodeId);
  };

  const getRelationshipLabel = (type: string) => {
    return relationshipTypes.find(rel => rel.value === type)?.label || type;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Knowledge Connections
          </CardTitle>
          <p className="text-sm text-black">
            Connect this content to other nodes to build the knowledge graph
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Connection Form */}
          <div className="space-y-3">
            <Select value={selectedRelationType} onValueChange={setSelectedRelationType}>
              <SelectTrigger>
                <SelectValue placeholder="Select relationship type..." />
              </SelectTrigger>
              <SelectContent>
                {relationshipTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-black">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black h-4 w-4" />
              <Input
                placeholder="Search for content to connect..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Search Results */}
            {searchTerm && filteredNodes && selectedRelationType && (
              <div className="max-h-40 overflow-y-auto space-y-2 border rounded-md p-2">
                {filteredNodes.slice(0, 5).map((node) => (
                  <div
                    key={node.id}
                    className="flex items-center justify-between p-2 hover:bg-muted rounded cursor-pointer"
                    onClick={() => addConnection(node)}
                  >
                    <div>
                      <div className="font-medium text-sm">{node.title}</div>
                      <div className="text-xs text-black">
                        {node.disciplines?.name} • {node.node_type}
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {filteredNodes.length === 0 && (
                  <p className="text-sm text-black text-center py-4">
                    No matching content found
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Current Connections */}
          {edges.length > 0 && (
            <div>
              <h4 className="font-medium mb-3">Current Connections ({edges.length})</h4>
              <div className="space-y-2">
                {edges.map((edge) => {
                  const targetNode = getNodeById(edge.targetNodeId);
                  return (
                    <div
                      key={edge.targetNodeId}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {targetNode?.title || 'Unknown Node'}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {getRelationshipLabel(edge.relationshipType)}
                          </Badge>
                          <span className="text-xs text-black">
                            {targetNode?.disciplines?.name} • {targetNode?.node_type}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeConnection(edge.targetNodeId)}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NodeConnectionInterface;
